"use client";

import React from "react";
import { Quote, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheEducationGapInGhana() {
  const {
    ref: topRowRef,
    isVisible: isTopRowVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: quoteRef,
    isVisible: isQuoteVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: bottomRowRef,
    isVisible: isBottomRowVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-education-gap-in-ghana" className="overflow-hidden bg-background py-12 sm:py-16 md:py-20 lg:py-24">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
        <div ref={topRowRef} className={`grid grid-cols-1 items-center gap-10 transition-all duration-1000 ease-out lg:grid-cols-2 lg:gap-16 xl:gap-20 ${isTopRowVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          <div className="order-2 space-y-5 lg:order-1 sm:space-y-6">
            <h2 className="text-3xl font-bold tracking-tight text-primary sm:text-4xl md:text-5xl">
              The Reality of the Education Gap
            </h2>

            <div className="space-y-4 text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
              <p>
                In many socially and economically disadvantaged areas of Ghana,
                a child’s potential is often limited not by ability, but by
                circumstance. Socioeconomic barriers continue to widen the
                education gap and leave many bright young minds behind.
              </p>
              <p>
                Schools often face overcrowded classrooms, weak infrastructure,
                and shortages of essential learning materials. For many
                families, the cost of education can also place children at risk
                of missing school or leaving early.
              </p>
            </div>
          </div>

          <div className="relative order-1 lg:order-2">
            <div className="absolute inset-0 -z-10 translate-x-3 translate-y-3 rounded-2xl bg-secondary/10 sm:translate-x-4 sm:translate-y-4" />
            <img src="https://touching-owl.10web.cloud/wp-content/uploads/2026/03/18.jpeg" alt="Students in a rural Ghanaian classroom" className="h-[260px] w-full rounded-2xl object-cover object-center shadow-xl sm:h-[320px] md:h-[380px] lg:h-[420px]" />
          </div>
        </div>

        <div ref={quoteRef} className={`relative mx-auto my-16 max-w-4xl px-4 py-8 text-center transition-all duration-1000 delay-200 ease-out sm:my-20 sm:px-8 sm:py-10 md:my-24 md:py-12 ${isQuoteVisible ? "scale-100 opacity-100" : "scale-95 opacity-0"}`}>
          <Quote className="absolute left-0 top-0 h-12 w-12 -translate-x-1 -translate-y-2 -rotate-12 text-secondary/20 sm:h-16 sm:w-16 md:h-24 md:w-24 md:-translate-x-8 md:-translate-y-8" />
          <Quote className="absolute bottom-0 right-0 h-12 w-12 translate-x-1 translate-y-2 rotate-180 text-secondary/20 sm:h-16 sm:w-16 md:h-24 md:w-24 md:translate-x-8 md:translate-y-8" />

          <blockquote className="relative z-10">
            <p className="font-serif text-xl font-medium leading-snug text-primary sm:text-2xl md:text-3xl lg:text-4xl">
              "Education is not just about reading and writing; it is the
              foundation upon which entire communities build their future and
              break the cycle of poverty."
            </p>
          </blockquote>
        </div>

        <div ref={bottomRowRef} className={`grid grid-cols-1 items-center gap-10 transition-all duration-1000 ease-out lg:grid-cols-2 lg:gap-16 xl:gap-20 ${isBottomRowVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          <div className="relative order-1">
            <div className="absolute inset-0 -z-10 -translate-x-3 translate-y-3 rounded-2xl bg-primary/5 sm:-translate-x-4 sm:translate-y-4" />
            <img src="https://touching-owl.10web.cloud/wp-content/uploads/2026/03/19.jpeg" alt="Students learning together in Ghana" className="h-[260px] w-full rounded-2xl object-cover object-center shadow-xl sm:h-[320px] md:h-[380px] lg:h-[420px]" />
          </div>

          <div className="order-2 space-y-6 sm:space-y-8">
            <div className="space-y-4 text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
              <p>
                This gap does not only affect individual children. It also
                limits community growth and can reinforce cycles of hardship
                across generations. We believe that where a child is born should
                not determine the quality of education they receive.
              </p>
              <p>
                By responding to these barriers with practical support,
                including improved learning environments, educational resources,
                and community-based initiatives, we aim to help create more
                equal opportunities for children to learn, grow, and succeed.
              </p>
            </div>

            <div className="pt-2 sm:pt-4">
              <Button asChild size="lg" className="group rounded-full px-6 sm:px-8">
                <Link to="/OurStoryPage">
                  Learn more about our story
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}